import { createFileRoute, Outlet } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { CircleIcon, SquareIcon } from 'lucide-react'
import { usePreviewMode } from '@/components/custom/route-transition/surface/route-preview'
import { rtEvents } from '@/components/custom/route-transition/core/debug'
import { TransitionTabsSurface } from './-components/shared/transition-tabs'

// THE FULL-CAPABILITY transition playground — the app's real mobile topology, in miniature and with no
// data behind it:
//
//   OUTER LATERAL   this layout: a two-member pager (One / Two), like the top-level section pager
//   HIERARCHICAL    inside One: a two-level nav stack (item list → pushed item screen), like a section
//   INNER LATERAL   inside the pushed screen: a four-tab pager (Alpha / Board / Gamma / Delta), like the
//                   report tabs — with Board deliberately as heavy as the real Tablero
//
// …so every coupling the sections exercise — a pager inside a stack inside a pager, the leading-edge
// chain that hands the inner pager's overscroll to the stack's back-swipe, the outer pager locked while
// the stack is above its root — is reproducible here in isolation. The tab strips are the SAME component
// the report renders (shared/transition-tabs.tsx), so strip behaviour can't drift either. Swipe on a
// touch device / device mode; the HUD (bottom-left) shows stack depth, both pagers' live positions, a
// GLITCH counter (any pane moving against an active finger), and the engine's recent events frozen at
// that moment.
export const Route = createFileRoute('/_app/dev-resources/transitions')({
  component: RouteComponent,
})

export const TRANSITIONS_SCOPE = '/dev-resources/transitions'

/**
 * The on-page glitch detector, across EVERY pager this playground mounts. A legitimate gesture never
 * moves against the finger: while a touch is down, each pager's visual position (scrollLeft − transform)
 * may only travel the way the drag pulls it — any pull-back > 18px from a gesture's extremum is counted
 * in red, with the engine's recent events (rtlog buffer) frozen alongside so the trigger is readable
 * right on the page.
 */
function TransitionHud() {
  const [view, setView] = useState({ line: '—', glitches: 0, events: [] as string[] })
  const frozen = useRef<string[] | null>(null)
  useEffect(() => {
    let raf = 0
    let touchDown = false
    let glitches = 0
    let lastText = ''
    const tracks = new Map<string, { anchor: number | null; dir: number }>()
    const pagers = () =>
      [...document.querySelectorAll<HTMLElement>('.rt-scroller')].flatMap((el) => {
        if (el.clientWidth === 0 || el.closest('[inert]')) return []
        const surface = el.closest('[data-rt-surface]')?.getAttribute('data-rt-surface') ?? ''
        return surface.startsWith(TRANSITIONS_SCOPE) ? [{ el, surface }] : []
      })
    const dump = () => rtEvents().map((e) => `#${e.n} ${e.t} ${e.evt}${e.data ? ' ' + JSON.stringify(e.data) : ''}`)
    const onTouch = (e: TouchEvent) => {
      touchDown = e.touches.length > 0
      if (!touchDown) tracks.clear()
    }
    document.addEventListener('touchstart', onTouch, { passive: true, capture: true })
    document.addEventListener('touchend', onTouch, { passive: true, capture: true })
    document.addEventListener('touchcancel', onTouch, { passive: true, capture: true })
    const tick = () => {
      raf = requestAnimationFrame(tick)
      const found = pagers()
      const parts: string[] = []
      const stage = document.querySelector<HTMLElement>(`[data-rt-surface="${TRANSITIONS_SCOPE}"] [data-ns-stage]`)
      if (stage) parts.push(`d${getComputedStyle(stage).getPropertyValue('--ns-depth').trim() || '0'}`)
      for (const { el, surface } of found) {
        const m = new DOMMatrixReadOnly(getComputedStyle(el).transform)
        const v = el.scrollLeft - m.e
        const w = el.clientWidth || 1
        const label = surface === TRANSITIONS_SCOPE ? 'outer' : surface.endsWith('/alpha') ? 'sub' : 'inner'
        parts.push(`${label} ${(v / w).toFixed(2)}`)
        if (touchDown) {
          const track = tracks.get(surface) ?? { anchor: null, dir: 0 }
          tracks.set(surface, track)
          if (track.anchor === null) track.anchor = v
          if (track.dir === 0) {
            if (Math.abs(v - track.anchor) > 6) track.dir = Math.sign(v - track.anchor)
          } else {
            track.anchor = track.dir > 0 ? Math.max(track.anchor, v) : Math.min(track.anchor, v)
            if (track.dir * (v - track.anchor) < -18) {
              glitches += 1
              frozen.current = dump()
              track.anchor = v
              track.dir = 0
            }
          }
        }
      }
      const text = `${parts.join(' · ')}·g${glitches}`
      if (text !== lastText) {
        lastText = text
        setView({ line: parts.join(' · '), glitches, events: (frozen.current ?? dump()).slice(-5) })
      }
    }
    raf = requestAnimationFrame(tick)
    return () => {
      cancelAnimationFrame(raf)
      document.removeEventListener('touchstart', onTouch, { capture: true })
      document.removeEventListener('touchend', onTouch, { capture: true })
      document.removeEventListener('touchcancel', onTouch, { capture: true })
    }
  }, [])
  return (
    <div className="pointer-events-none fixed bottom-2 left-2 z-50 max-w-[95vw] rounded bg-black/85 p-2 font-mono text-[10px] leading-tight text-white">
      <div>
        <span className="text-cyan-300">{view.line}</span> · glitches{' '}
        <span className={view.glitches > 0 ? 'font-bold text-red-400' : 'text-green-400'}>{view.glitches}</span>
      </div>
      {view.events.map((line) => (
        <div key={line} className="max-w-[90vw] truncate text-white/60">
          {line}
        </div>
      ))}
    </div>
  )
}

function RouteComponent() {
  // Previews route through here bare (the HUD and the outer strip are live-tree chrome); the tab surface
  // itself gates its own previews, but the HUD must not render into preview panes either.
  if (usePreviewMode(TRANSITIONS_SCOPE) === 'passthrough') return <Outlet />
  return (
    <div className="flex min-h-[70vh] flex-col">
      <TransitionTabsSurface
        scope={TRANSITIONS_SCOPE}
        // PROTOTYPE, and the playground is deliberately the only surface that turns it on: the arriving tab is
        // touchable before the transition has finished — tap a button on it during the settle, or catch a
        // settling pager and tap instead of dragging. See `interactiveIncoming` for the rule and its costs.
        //
        // ONLY THE GESTURE ENGINE CAN DO THIS, and the scroll-snap engine is the default — a native scroll owns
        // every touch once it has claimed the pan, so it ignores the prop. To try this, run the app with
        // `SCROLL_TRANSITIONS_ENABLED=false` and use the probe button on the Two pane.
        interactiveIncoming
        tabs={[
          { segment: 'one', label: 'One (stack)', icon: SquareIcon },
          { segment: 'two', label: 'Two', icon: CircleIcon },
        ]}
      />
      <TransitionHud />
    </div>
  )
}
